import { Done, PlayArrow } from "@mui/icons-material";
import { Box, CircularProgress, Skeleton } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import "../styles/generatedWorkflow.css";
import "../styles/commonStyles.css";
import { CheckCircleIcon } from "../svgUrls";
import RunOnDataDialog from "./RunOnDataDialog";
import { Node } from "../types/workflow";
import { WorkflowServiceIcon } from "./Common/WorkflowServiceIcon";

type GeneratedWorkflowProps = {
    generatedWorkflow: Node[] | null;
    hasDemoRun: boolean[];
    setHasDemoRun: React.Dispatch<React.SetStateAction<boolean[]>>;
    loadingNewWorkflow: boolean;
}

const GeneratedWorkflow: React.FC<GeneratedWorkflowProps> = ({
    generatedWorkflow,
    hasDemoRun,
    setHasDemoRun,
    loadingNewWorkflow,
}) => {
    const [stepUnderProcess, setStepUnderProcess] = useState(0);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const t = useRef(0);

    const closeDialog = () => {
        setIsDialogOpen(false);
    };

    useEffect(() => {
        if (generatedWorkflow?.length && stepUnderProcess > generatedWorkflow.length) {
            clearInterval(t.current);
            setHasDemoRun([...hasDemoRun, true]);

            t.current = window.setTimeout(function() {
                const scrollingElement = document.getElementById("generatedWorkflow");
                if (scrollingElement) {
                    scrollingElement.scrollTop = scrollingElement.scrollHeight;
                }
            }, 500);
        }
    }, [stepUnderProcess, generatedWorkflow, hasDemoRun, setHasDemoRun]);

    useEffect(() => {
        setStepUnderProcess(0);
        t.current = window.setInterval(() => setStepUnderProcess((v) => v + 1), 100);

        return () => {
            clearInterval(t.current);
        };
    }, [generatedWorkflow]);

    return (
        <>
            <Box sx={{ fontWeight: 600, fontSize: 10, borderBottom: "1px solid #ECEDF1", color: "#7E86A0", textAlign: "center", padding: 1 }}>
                GENERATED WORKFLOW
            </Box>
            <Box sx={{ overflow: "auto", height: stepUnderProcess > (generatedWorkflow?.length || 0) ? "calc(100% - 80px)" : "calc(100% - 30px)" }} className="noScrollbar">
                {loadingNewWorkflow ? (
                    Array.from({ length: 6 }, (_, i) => (
                        <Box key={i} sx={{ display: "flex", alignItems: "center" }}>
                            <Skeleton variant="circular" animation="wave" sx={{ bgcolor: "#f5f6f7", height: 20, width: 28, borderRadius: 11, margin: 3 }} />
                            <Skeleton variant="rectangular" width={"calc(100% - 80px)"} height={50} sx={{ margin: 2, bgcolor: "#f5f6f7" }} animation="wave" />
                        </Box>
                    ))
                ) : generatedWorkflow?.length && Array.isArray(generatedWorkflow) ? (
                    generatedWorkflow.map((node, i) => {
                        const index = i + 1;
                        return (
                            <Box key={node.id}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Box sx={{ width: "70px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                                        {index > stepUnderProcess ? (
                                            <Box className="stepNumber">{index}</Box>
                                        ) : index === stepUnderProcess ? (
                                            <Box className="stepNumberInProgress">
                                                <CircularProgress size={15} className="inProgressIcon" />
                                            </Box>
                                        ) : (
                                            <Box className="stepNumberDone">
                                                <Done className="doneIcon" />
                                            </Box>
                                        )}
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: "16px", margin: "16px 0", flex: 1 }}>
                                        <Box>
                                            <WorkflowServiceIcon
                                                workflowType={node.service}
                                                height={35}
                                                width={35}
                                                alt={node.name}
                                                className="workflow-service-icon"
                                                style={{ border: "1px solid lightgrey", borderRadius: "5px", padding: "5px", backgroundColor: "rgba(211, 211, 211, 0.08)" }}
                                            />
                                        </Box>
                                        <Box>
                                            <Box sx={{ fontWeight: 500 }}>{node.id}</Box>
                                            <Box sx={{ fontSize: 12, color: "#536074", marginTop: "4px" }}>{node.name}</Box>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ borderBottom: "1px solid #ECEDF1", marginLeft: "70px" }}>
                                    {stepUnderProcess === index ? (
                                        <Box className="demoUnderProcess">
                                            Processing...
                                        </Box>
                                    ) : stepUnderProcess > index ? (
                                        <Box className="demoRan">
                                            <CheckCircleIcon height={12} width={12} />
                                            <Box sx={{ flex: 1 }}>{node.config}</Box>
                                        </Box>
                                    ) : null}
                                </Box>
                            </Box>
                        );
                    })
                ) : null}
            </Box>
            {generatedWorkflow?.length && stepUnderProcess > generatedWorkflow.length ? (
                <Box className="runOnData" onClick={function() {
                    window.location.href = `/workflow/setup/${generatedWorkflow && generatedWorkflow[0]?.workflow.id}`;
                }}>
                    <PlayArrow height={12} width={12} />
                    Run on your data
                </Box>
            ) : null}
            <RunOnDataDialog isDialogOpen={isDialogOpen} closeDialog={closeDialog} />
        </>
    );
};

export default GeneratedWorkflow;
